import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, History, RefreshCw, Upload, Workflow } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService, getApiErrorMessage, ImportJob, MetadataExport, PromptRun } from '../services/api';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import { processingRealtimeStore } from '../services/processingRealtimeStore';

interface AutomationExecutionHistoryProps {
  indexId: string;
  runs?: PromptRun[];
  onImportActivity?: () => Promise<void>;
}

const AutomationExecutionHistory: React.FC<AutomationExecutionHistoryProps> = ({ indexId, runs = [], onImportActivity }) => {
  const navigate = useNavigate();
  const [imports, setImports] = useState<ImportJob[]>([]);
  const [exports, setExports] = useState<MetadataExport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const outputRevisions = useRef(new Map<string, number>());
  const canObserve = usePassiveApiActivity(
    imports.some((job) => isInProgressStatus(job.status))
    || exports.some((job) => isInProgressStatus(job.status))
    || runs.some((run) => isInProgressStatus(run.status)),
  );
  const runKey = runs.map((run) => run.run_id).sort().join(',');

  useEffect(() => {
    if (!canObserve) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      apiService.getImportJobs(undefined, indexId, 20, true),
      apiService.getExports(20, indexId, true),
    ]).then(([nextImports, nextExports]) => {
      if (cancelled) return;
      outputRevisions.current = new Map(nextImports.map((job) => [job.job_id, job.progress.parts_completed || job.progress.imported || 0]));
      setImports(nextImports);
      setExports(nextExports);
    }).catch((failure) => {
      if (!cancelled) setError(getApiErrorMessage(failure, 'Automation history is unavailable.'));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canObserve, indexId, revision]);

  const progressScopeKey = JSON.stringify({ index_id: indexId,
    import_job_ids: imports.map((job) => job.job_id), export_ids: exports.map((job) => job.export_id),
    run_ids: runKey.split(',').filter(Boolean),
  });
  useEffect(() => {
    if (!canObserve) return;
    return processingRealtimeStore.subscribe((event) => {
      const snapshot = event.payload?.snapshot;
      if (!snapshot) return;
      if (event.event_type === 'import_job.progress') {
        const nextRevision = snapshot.outputs_revision || 0;
        const previous = outputRevisions.current.get(snapshot.job_id) || 0;
        outputRevisions.current.set(snapshot.job_id, nextRevision);
        if (nextRevision > previous && onImportActivity) void onImportActivity().catch(() => setError('New media is available. Refresh to load it.'));
        setImports((current) => current.map((job) => job.job_id === snapshot.job_id ? { ...job, ...snapshot } : job));
      } else if (event.event_type === 'export.progress') {
        setExports((current) => current.map((job) => job.export_id === snapshot.export_id ? { ...job, ...snapshot } : job));
      }
    }, JSON.parse(progressScopeKey));
  }, [canObserve, progressScopeKey, onImportActivity]);

  const rows = useMemo(() => [
    ...imports.map((job) => ({
      id: job.job_id, kind: 'import' as const, time: job.created_at,
      runId: null,
      status: job.status, detail: `${job.progress.imported} imported · ${job.progress.skipped} skipped`,
    })),
    ...exports.map((job) => ({
      id: job.export_id, kind: 'export' as const, time: job.created_at,
      runId: null,
      status: job.queue_status === 'queued' ? 'pending' : job.status,
      detail: job.destination_subpath || 'Connected storage',
    })),
    ...runs.filter((run) => imports.some((job) => job.prompt_run_id === run.run_id)).map((run) => ({
      id: run.run_id, kind: 'processing' as const, time: run.executed_at, runId: run.run_id,
      status: run.status, detail: run.prompt_name || 'Saved engine',
    })),
  ].sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime()).slice(0, 20), [imports, exports, runs]);

  return (
    <section className="automation-execution-history" aria-label="Automation execution history" aria-busy={loading}>
      <div className="automation-section-heading">
        <History size={15} /><strong>Recent executions</strong>
        <button type="button" className="automations-icon-button" onClick={() => setRevision((value) => value + 1)}
          disabled={loading} aria-label="Refresh automation history" title="Refresh automation history">
          <RefreshCw size={14} className={loading ? 'automations-spinning' : undefined} />
        </button>
      </div>
      {error ? <p className="automations-field-error" role="alert">{error}</p> : loading && rows.length === 0 ? (
        <p>Loading execution history…</p>
      ) : rows.length === 0 ? <p>No automation executions for this index yet.</p> : (
        <ol className="automation-history-list">
          {rows.map((row) => (
            <li key={`${row.kind}:${row.id}`}>
              {row.kind === 'import' ? <Upload size={15} /> : row.kind === 'processing' ? <Workflow size={15} /> : <Download size={15} />}
              <div>
                {row.runId ? <button type="button" className="automation-history-run-link"
                  onClick={() => navigate(`/workflows?index=${encodeURIComponent(indexId)}&run=${encodeURIComponent(row.runId!)}`)}>Processing</button>
                  : <strong>{row.kind === 'import' ? 'Import' : 'Export'}</strong>}
                <small>{row.detail}</small>
              </div>
              <span className={`automations-status automations-status--${row.status === 'failed' ? 'error' : row.status === 'completed' ? 'active' : 'loading'}`}>
                {row.status === 'pending' ? 'Queued' : row.status.replace(/_/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase())}
              </span>
              {row.time && <time dateTime={row.time}>{new Date(row.time).toLocaleString()}</time>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default AutomationExecutionHistory;
